<script setup lang="ts">

import { ref, computed, watch, onMounted, onUnmounted } from "vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import axios from "axios"
import { useToast } from "@/components/ui/toast"
import { useI18n } from 'vue-i18n';
import SingleSelect from './SingleSelect.vue'
import ManySelect from './ManySelect.vue'
import TiptapEditor from './TiptapEditor.vue'

import { optionLabel, capitalize } from '@/utils/globals'
import { Check, X } from 'lucide-vue-next'

const { toast } = useToast()

const { t, locale } = useI18n();

// Map i18n locale to date locale string
const dateLocale = computed(() => {
  const localeMap: Record<string, string> = {
    'de': 'de-DE',
    'en': 'en-US',
    'zh-TW': 'zh-TW'
  }
  return localeMap[locale.value] || 'de-DE'
})

const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
if (token) axios.defaults.headers.common["X-CSRF-Token"] = token


const props = withDefaults(defineProps<{
  columns: ColumnDef[]
  currentItem: Object
  label: string
  labelSingular?: string
  logo?: string | null
  endpoint?: string
  saveendpoint?: string
  endpointpars?: Record<string, number>
  enableSearch?: boolean
  enableAdd?: boolean
  enableEdit?: boolean
  enableDetail?: boolean
  enableDelete?: boolean
  enableFilter?: boolean
  enableColChange?: boolean
  enableColFilterSide?: boolean
  enableColFilterTop?: boolean
  alwaysEdit?: boolean
  withpaging?: boolean
  hideActionColumn?: boolean
  headerFontSize?: number
  singleSelectFontsize?: number
  data?: any[] 
  onDelete?: (id: number) => void
  dynamicOptions?: Record<string, any[]>
}>(), {
  saveendpoint: null,
  logo: null,
  enableSearch: false,
  enableAdd: true,
  enableEdit: true,
  enableDetail: true,
  enableDelete: true,
  enableFilter: true,
  enableColChange: true,
  enableColFilterSide: true,
  enableColFilterTop: false,
  alwaysEdit: false,
  withpaging: true,
  hideActionColumn: false,
})


function getNested(obj: any, path: string) {
  let psplit = path.split(".");
  return psplit.reduce((o, k) => (o ? o[k] : undefined), obj)
}

function isBooleanValue(value: any): boolean {
  if (value === true || value === 1 || value === "1" || value === "true" || value === "Ja" || value === "Yes") {
    return true
  }
  return false
}

function setNested(obj: any, path: string, value: any, event: Event) {
  const keys = path.split(".");
  let o = obj;

  keys.slice(0, -1).forEach((k) => {
    if (!o[k]) o[k] = {};
    o = o[k];
  });

  o[keys[keys.length - 1]] = value;
}

const emit = defineEmits<{(e: 'close-editor'): void; (e: 'save-editor'): void;}>();


function formatISOForDateInput(val: string | undefined | null) {
  if (!val) return ''
  // If val contains a timestamp, strip it
  return val.split('T')[0]
}

function toSelectHash(options, arr) {
	console.log("JSON.stringify(options)=" + JSON.stringify(options));
	console.log("JSON.stringify(arr)=" + JSON.stringify(arr));
	if (arr == null || arr == "") {
		return {}
	}
	  return arr.reduce((acc, obj) => {
	    acc[obj.id] = optionLabel(options, obj);
	    return acc;
	  }, {});
}

function toFilterHash(options, arr) {
	if (arr == null) {
	    return [];
	} 
	
	console.log("arr=" + JSON.stringify(arr))
	return toSelectHash(options, arr.map(item => ({ id: item })))
}

function toCollection(list) {
  if (list == null) {
	return []
  }
  return Array.isArray(list) ? list : Object.values(list);
}

function toggleManyEdit(col, currentItem, oid, val) {
  let k = col.key;
  let currentValue = getNested(currentItem, k);
  
  // Initialize as empty array if null/undefined
  if (!currentValue) {
    setNested(currentItem, k, []);
    currentValue = [];
  }
  
  if (val) {
    // Add the option - find it from col.options or props.dynamicOptions
    const options = col.options || props.dynamicOptions?.[col.key] || [];
    const missingOption = options.find(opt => opt.id === oid);
    if (missingOption) {
      // Check if already exists
      const exists = currentValue.some(item => {
        const itemId = typeof item === 'object' && item !== null ? item.id : item;
        return itemId === oid;
      });
      if (!exists) {
        setNested(currentItem, k, [...currentValue, missingOption]);
      }
    }
  } else {
    // Remove the option
    const filtered = currentValue.filter(item => {
      const itemId = typeof item === 'object' && item !== null ? item.id : item;
      return itemId !== oid;
    });
    setNested(currentItem, k, filtered);
  }
}

const menuVisible = ref(false)
const menuX = ref(0)
const menuY = ref(0)
const selectedCellContent = ref('')
const hoverMenuItem = ref(false)

function showContextMenu(event: MouseEvent, content: string) {
  // Only trigger for secondary button (right click)
  if (event.button !== 2) return
  event.preventDefault()
  event.stopPropagation()

  selectedCellContent.value = content

  // Prevent menu from going outside the viewport
  const menuWidth = 140
  const menuHeight = 36
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  menuX.value = event.clientX + menuWidth > viewportWidth ? viewportWidth - menuWidth - 10 : event.clientX
  menuY.value = event.clientY + menuHeight > viewportHeight ? viewportHeight - menuHeight - 10 : event.clientY

  menuVisible.value = true
}

function hideContextMenu() {
  menuVisible.value = false
}

function copyToClipboard() {
  navigator.clipboard.writeText(selectedCellContent.value).then(() => {
    toast({ title: "Copied", description: selectedCellContent.value })
    hideContextMenu()
  })
}

function onFileChange(event: Event, key: string, currentItem) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0] || null;

  if (file) {
    // Store the file temporarily in your currentItem
    setNested(currentItem, key, file);
  } else {
    // Clear if no file selected
    setNested(currentItem, key, null);
  }
}

// Hide menu when clicking anywhere else
onMounted(() => window.addEventListener('click', hideContextMenu))
onUnmounted(() => window.removeEventListener('click', hideContextMenu))

</script>

<template>
	
	<!-- Dedicated Editor Panel -->
	<div
	  class="fixed inset-0 z-[10001] flex items-start justify-center pt-20"
	  @click.stop
	>
	  <!-- Backdrop -->
	  <div
	    class="fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
	    @click="$emit('close-editor')"
	  ></div>

	  <!-- Modal / Panel -->
	  <div
	    class="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[85vh] z-10 flex flex-col animate-in fade-in-50 zoom-in-95"
	  >
	    <!-- Header (sticky, styled) -->
	    <div
	      class="px-6 py-4 border-b bg-gradient-to-r from-gray-50 to-white sticky top-0 z-20 flex justify-between items-center rounded-t-2xl"
	    >
	      <h2 class="text-lg font-semibold text-gray-800">
	        {{ capitalize(props.label) }}
	      </h2>
	      <button
	        @click="$emit('close-editor')"
	        class="text-gray-400 hover:text-gray-600 transition-colors"
	      >
	        ✖
	      </button>
	    </div>

	    <!-- Body (scrollable) -->
	  <div class="px-6 py-4 overflow-y-auto flex-1 space-y-4">
	    <div v-if="currentItem">
	      <div
	        v-for="col in props.columns.filter(c => c.key !== 'actions')"
	        :key="col.key"
	        class="p-4 border rounded-lg bg-gray-50 shadow-sm space-y-2"
	      >
	        <!-- Field Label -->
	        <label
	          class="block text-base font-bold text-gray-700 tracking-wide mb-2"
	        >
	          {{ col.label }}
	        </label>

	        <!-- READONLY FIELDS -->
	          <template v-if="!col.write">
	          <template v-if="col.type === 'boolean' || col.edit === 'checkbox'">
	            <div class="flex items-center">
	              <Check
	                v-if="isBooleanValue(getNested(currentItem, col.key))"
	                class="w-5 h-5 text-green-600"
	              />
	              <X
	                v-else
	                class="w-5 h-5 text-red-600"
	              />
	            </div>
	          </template>

	          <template v-else-if="col.type === 'select' || col.type === 'multi-select'">
	            {{
	              (col.options || props.dynamicOptions?.[col.key] || []).find(
	                opt => opt.id == getNested(currentItem, col.key)
	              )?.label || getNested(currentItem, col.key)
	            }}
	          </template>

	          <template v-else-if="col.type === 'many'">
	            {{ toCollection(getNested(currentItem, col.key)).map(u => optionLabel(col.options, u)).join(',') }}
	          </template>

	          <template v-else-if="col.type === 'color'">
	            <div class="flex items-center gap-2">
	              <div class="w-4 h-4 rounded-full border" :style="{ backgroundColor: getNested(currentItem, col.key) }"></div>
	              <span class="text-xs text-gray-600">{{ getNested(currentItem, col.key) }}</span>
	            </div>
	          </template>

	          <template v-else-if="col.type === 'date'">
	            {{
	              getNested(currentItem, col.key)
	                ? new Date(getNested(currentItem, col.key)).toLocaleDateString(dateLocale, {
	                    day: "2-digit",
	                    month: "2-digit",
	                    year: "numeric",
	                  })
	                : ""
	            }}
	          </template>

	          <template v-else-if="col.type === 'text'">
	                <div class="truncate" :style="{ maxWidth: '100% !important', whiteSpace: 'normal' }"
					  @contextmenu.prevent="showContextMenu($event, getNested(currentItem, col.key))">
	                  {{ getNested(currentItem, col.key) }}
	                </div>
	          </template>

	          <template v-else>
	            {{ getNested(currentItem, col.key) }}
	          </template>
	        </template>

	        <!-- WRITABLE FIELDS -->
	        <template v-else>
	          <template v-if="!col.type || col.edit === 'text'">
	            <Input
	              :modelValue="getNested(currentItem, col.key)"
	              @update:modelValue="val => setNested(currentItem, col.key, val)"
	              class="w-full"
	            />
	          </template>
	          <template v-else-if="col.edit === 'text_area'">
	            <textarea
	              :value="getNested(currentItem, col.key)"
	              @input="setNested(currentItem, col.key, $event.target.value)"
	              rows="6"
	              class="w-full border rounded-lg p-2 text-sm focus:ring focus:ring-blue-200"
	            ></textarea>
	          </template>
	          <template v-else-if="col.type === 'number'">
	            <input
	              type="number"
	              :value="getNested(currentItem, col.key)"
	              @input="setNested(currentItem, col.key, $event.target.valueAsNumber)"
	              class="border rounded-lg p-2 text-sm w-full"
	            />
	          </template>
	          <template v-else-if="col.type === 'date'">
	            <input
	              type="date"
	              :value="formatISOForDateInput(getNested(currentItem, col.key))"
	              @input="setNested(currentItem, col.key, $event.target.value)"
	              class="border rounded-lg p-2 text-sm w-full"
	            />
	          </template>
	          <template v-else-if="col.type === 'boolean' || col.edit === 'checkbox'">
	            <label class="flex items-center gap-2">
	              <input
	                type="checkbox"
	                :checked="isBooleanValue(getNested(currentItem, col.key))"
	                @change="setNested(currentItem, col.key, $event.target.checked, $event.shiftKey)"
	                class="h-4 w-4"
	              />
	              <span class="text-sm">Yes</span>
	            </label>
	          </template>
			  <template v-else-if="col.type === 'file'">
			    <input
			      type="file"
			      @change="onFileChange($event, col.key, currentItem)"
			      class="border rounded-lg p-2 text-sm w-full"
			    />
			  </template>
			  <template v-else-if="col.type === 'text' && col.edit === 'richtext'">
				<TiptapEditor
				  :model-value="getNested(currentItem, col.key)"
				  @update:model-value="val => setNested(currentItem, col.key, val)"
				/>
			  </template>
			  <template v-else-if="col.type === 'many'">
			      <ManySelect  :col="col"
			  	             :selectedOptions="toSelectHash(col.options || props.dynamicOptions?.[col.key] || [], getNested(currentItem, col.key))"
			  				 :dynamicOptions="props.dynamicOptions || {}"
			  				 :toggleOption="(oid, val) => toggleManyEdit(col, currentItem, oid, val)"	/>
			  </template>
	          <template v-else-if="col.type === 'select' || col.type === 'multi-select'">
				<SingleSelect
				  :options="col.options"
				  :modelValue="getNested(currentItem, col.key)"
				  @update:modelValue="val => setNested(currentItem, col.key, val)"
				/>
	          </template>
	        </template>
	      </div>
		  <div style="height: 500px	">
		      <slot name="extra" :item="currentItem" />
		  </div>
	    </div>
	  </div>

	    <!-- Footer (sticky, styled) -->
	    <div class="px-6 py-4 border-t bg-gray-50 sticky bottom-0 z-20 flex justify-end gap-3 rounded-b-2xl">
	      <Button
	        class="bg-blue-600 text-white hover:bg-blue-700"
			@click.stop
	        @click="$emit('save-editor')"
	      >
	        {{t('view.btn_save')}}
	      </Button>		  
	      <Button variant="outline" @click.stop @click="$emit('close-editor')">
		  Cancel
	      </Button>
	    </div>
	  </div>
	</div>

		<!-- Custom left-click context menu -->
    <div v-if="menuVisible"
    :style="{
    position: 'fixed',
    top: menuY + 'px',
    left: menuX + 'px',
    background: '#fff',
    border: '1px solid #ccc',
    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    zIndex: 2000,
    minWidth: '120px',
    }"
    >
    <div
      @click="copyToClipboard"
      @mouseover="hoverMenuItem = true"
      @mouseleave="hoverMenuItem = false"
      :style="{ padding: '6px 12px', cursor: 'pointer', backgroundColor: hoverMenuItem ? '#eee' : 'transparent' }"
    >
      {{ t('view.copy') }}
    </div>
	</div>

</template>